import React, { useMemo } from 'react';
import { Box, CalendarIcon, DateRangeDropdown, List, PaginationBar, Text } from '@common-fe/common-fe';

import { LoginHistory } from './loginHistory.types';
import LoginHistoryListItem from './LoginHistoryListItem';

interface Props {
  loginHistory?: LoginHistory[];
  isLoading?: boolean;
  placeholder?: React.ReactNode;
  total: number;
  page: number;
  setPage: (value: number) => void;
  perPage: number;
  setPerPage: (value: number) => void;
  startDate: Date | null;
  endDate: Date | null;
  setPeriod: (value: [Date | null, Date | null]) => void;
}

const LoginHistoryList: React.FC<Props> = ({
  loginHistory,
  isLoading,
  placeholder,
  total,
  page,
  setPage,
  perPage,
  setPerPage,
  startDate,
  endDate,
  setPeriod
}) => {
  const children = useMemo(() => {
    if (isLoading) {
      return (
        <List.Preloader testId="login-history-list_preloader" />
      );
    }

    if (!loginHistory?.length) {
      return placeholder;
    }

    return loginHistory?.map((loginHistoryItem) => (
      <LoginHistoryListItem
        key={loginHistoryItem?.id}
        id={loginHistoryItem?.id}
        createdAt={loginHistoryItem?.createdAt}
        username={loginHistoryItem?.username}
        eventType={loginHistoryItem?.eventType}
        statusType={loginHistoryItem?.statusType}
        failureReasonType={loginHistoryItem?.failureReasonType}
        personalEmail={loginHistoryItem?.personalEmail}
        employeeIds={loginHistoryItem?.employeeIds}
        contactId={loginHistoryItem?.contactId}
        ipAddress={loginHistoryItem?.ipAddress}
        geolocation={loginHistoryItem?.geolocation}
        userAgent={loginHistoryItem?.userAgent}
        sourceType={loginHistoryItem?.sourceType}
        mfaStatusType={loginHistoryItem?.mfaStatusType}
      />
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, loginHistory]);

  return (
    <Box
      data-testId="login-history-list_wrapper"
      pad="spacing24"
      background="module"
      round="moduleRound"
    >
      <Box direction="row" align="center" justify="between">
        <Text weight={700}>Logs: {total}</Text>

        <Box width={{ min: 'control' }}>
          <DateRangeDropdown
            prefix="Period:"
            onChange={setPeriod}
            startDate={startDate}
            endDate={endDate}
            disabled={!total && !startDate && !endDate}
            icon={<CalendarIcon color="iconPrimary" />}
            ellipsisMode
          />
        </Box>
      </Box>

      <Box margin={{ top: 'spacing24' }}>
        <List>
          {!!loginHistory?.length && (
            <List.Header testId="login-history-list_header">
              <Box width="24px" margin={{ right: 'spacing16' }} />
              <List.Header.Item>
                Timestamp
              </List.Header.Item>
              <List.Header.Item>
                Username
              </List.Header.Item>
              <List.Header.Item>
                Event
              </List.Header.Item>
              <List.Header.Item>
                Login status
              </List.Header.Item>
              <List.Header.Item>
                Reason
              </List.Header.Item>
            </List.Header>
          )}
          
          {children}
        </List>
      </Box>
      
      {!!loginHistory?.length && (
        <PaginationBar
          total={total}
          page={page}
          onChangePage={setPage}
          pageSize={perPage}
          onChangePageSize={setPerPage}
        />
      )}
    </Box>
  );
};

export default LoginHistoryList;
